import Radium from '@flipdish/radium';
import * as React from 'react';
import { ListGroup } from 'react-bootstrap';
import { AutoAffix } from 'react-overlays';
import { Track } from '../../analytics';
import * as Api from '../../Api';
import * as B from '../../BasketControl';
import * as Routes from '../../Routes';
import * as Store from '../../Store';

const tinycolor = require('tinycolor2');
const uuidv4 = require('uuid/v4');

interface Props {
  Restaurant: Routes.RouteRestaurant;
  Items: B.Data;
  OnClick: () => any;
  RemoveItemAt: (payload: { menuId: number; index: number; indexName?: string }) => void;
  AddItem: (payload: B.Item) => void;
  InsideDeliveryZone: boolean;
  parentComponent: JSX.Element;
  Offset: number;
  Screen: string;
  vrid: number;
  translate: any;
  AppDesignScheme: Api.AppDesignScheme;
  Languages?: Store.Language[];
  isDark: boolean;
  MenuId: number;
}

interface State {}

export class Basket extends React.Component<Props, State> {
  private buttonState = (): { message: string; disabled: boolean } => {
    const { translate, InsideDeliveryZone, Items, Restaurant } = this.props;
    // TODO - check against minimun order value.
    const tooFewItems = Items.items.length < 1;

    if (!InsideDeliveryZone) {
      return {
        disabled: true,
        message: translate('Cannot_deliver_checkout')
      };
    }

    if (Restaurant.status === 'success' && Restaurant.data.IsOpen) {
      return {
        disabled: tooFewItems,
        message: translate('Checkout')
      };
    }

    if (Restaurant.status === 'success' && Restaurant.data.AcceptPreOrders) {
      return {
        disabled: tooFewItems,
        message: translate('Preorder')
      };
    }

    return {
      disabled: true,
      message: translate('Closed')
    };
  };

  public render() {
    const {
      Restaurant,
      Items,
      parentComponent,
      Offset,
      Screen,
      AppDesignScheme,
      isDark,
      Languages,
      vrid,
      RemoveItemAt,
      AddItem,
      OnClick,
      translate
    } = this.props;

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      primaryDark: {
        color: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString()
      },
      secondary: {
        color: AppDesignScheme.Secondary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      },
      tertiaryLight: {
        color: tinycolor(AppDesignScheme.Tertiary)
          .lighten(15)
          .toString()
      },
      basketHeader: {
        background: AppDesignScheme.Secondary
      },
      basket: {
        background: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        WebkitBoxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString()
      },
      darkBasket: {
        background: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        WebkitBoxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()
      },
      price: {
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString()
      },
      item: {
        borderColor: AppDesignScheme.Secondary
      },
      darkItem: {
        borderColor: AppDesignScheme.Primary
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {}
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      buttonHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      control: {
        color: AppDesignScheme.Tertiary,
        ':hover': {
          color: AppDesignScheme.Secondary
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Secondary)
            .darken(10)
            .toString()
        }
      },
      darkControl: {
        color: AppDesignScheme.Primary,
        ':hover': {
          color: AppDesignScheme.Secondary
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Secondary)
            .darken(10)
            .toString()
        }
      }
    };

    switch (Restaurant.status) {
      case 'pending':
      case 'none':
        return null;
      default:
        break;
    }

    let initialLang = 'en';

    if (Languages) {
      for (const Language of Languages) {
        if (Language.active) {
          initialLang = Language.code;
          break;
        }
      }
    }

    const total = B.ToCurrency(B.Total(Items.items), Restaurant.data.Currency, initialLang);

    //Convert array into an object with number of occurences
    const counts: any = {};

    for (let i = 0; i < Items.items.length; i++) {
      const item = Items.items[i];

      const key =
        item.Item.Name +
        '//' +
        item.Options.map((o) => o.Name).join(', ') +
        '//' +
        B.ItemTotal(item);

      if (counts[key]) {
        counts[key].occurence = counts[key].occurence ? counts[key].occurence + 1 : 1;
        counts[key].index = i;
        counts[key].item = item;
      } else {
        counts[key] = { occurence: 1, index: i, item };
      }
    }

    //Map object to an array of JSX elements
    const itemList: JSX.Element[] = [];

    for (const key in counts) {
      if (!counts.hasOwnProperty(key)) {
        continue;
      }

      const itemArray = key.split('//');
      const name = itemArray[0];
      const options = itemArray[1];
      const price = parseFloat(itemArray[2]);
      const occurence = counts[key].occurence;
      const index = counts[key].index;
      const basketItem = counts[key].item;

      const subtractItem = () => {
        Track('Restaurants', 'Remove Item From Basket Via Basket', {
          eventLabel: basketItem.Item.Name,
          virtualRestaurantId: vrid,
          menuId: this.props.MenuId
        });

        RemoveItemAt({
          menuId: this.props.MenuId,
          index,
          indexName: basketItem.Item.Name
        });
      };

      const addItem = () => {
        Track('Restaurants', 'Add Item To Basket Via Basket', {
          eventLabel: basketItem.Item.Name,
          virtualRestaurantId: vrid,
          name: basketItem.Item.Name,
          itemType: basketItem.Options.length === 0 ? 'simple' : 'complex',
          menuId: this.props.MenuId
        });

        AddItem({
          Item: basketItem.Item,
          Options: basketItem.Options
        });
      };

      const optionsList = options.split(',').map((option) => (
        <p key={uuidv4()} className="secondary clear-margins fd-h4" style={styles.secondary}>
          <i>{option}</i>
        </p>
      ));

      const item: JSX.Element = (
        <div
          className="flex menu__basket__content__item flex-justify-between"
          key={key}
          style={isDark ? styles.darkItem : styles.item}
        >
          <div className="menu__basket__content__item__options">
            <p
              tabIndex={0}
              aria-label={occurence + name}
              className="tertiary clear-margins fd-h4"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {' '}
              {`${occurence} x ${name}`}
            </p>
            {optionsList}
          </div>

          <div className="text-center menu__basket__content__item__price" style={styles.price}>
            <p className="secondary clear-margins bold text-center fd-h4" style={styles.secondary}>
              {B.ToCurrency(price * occurence, Restaurant.data.Currency, initialLang)}
            </p>
            <div className="flex flex-justify-center flex-align-center  menu__basket__content__item__price__controls">
              <button
                style={{
                  background: 'none',
                  color: 'inherit',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
                onClick={subtractItem}
                aria-label={`${translate('Remove')}` + name}
              >
                <i
                  className="tertiary icon_minus_alt2"
                  style={isDark ? styles.darkControl : styles.control}
                  key={`basket-minus-${key}`}
                />
              </button>
              <p
                className="tertiary clear-margins fd-h4"
                style={isDark ? styles.primary : styles.tertiary}
              >
                {occurence}
              </p>
              <button
                style={{
                  background: 'none',
                  color: 'inherit',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
                onClick={addItem}
                aria-label={`${translate('Add')}` + name}
              >
                <i
                  className="tertiary icon_plus_alt2"
                  style={isDark ? styles.darkControl : styles.control}
                  key={`basket-plus-${key}`}
                />
              </button>
            </div>
          </div>
        </div>
      );

      itemList.push(item);
    }

    let basketContents: JSX.Element | null = null;
    const { message, disabled } = this.buttonState();

    if (Items.items.length > 0) {
      basketContents = (
        <div className="menu__basket__content">
          <ListGroup className={`basket-item-list basket-item-list--${Screen}`}>
            {itemList}
          </ListGroup>
          <button
            accessKey="c"
            className="fd-button fd-button--full-width fd-button--secondary"
            disabled={disabled}
            onClick={OnClick}
            key="checkout-button"
            data-fd="basket-checkout"
            style={
              [
                styles.buttonSecondary,
                Radium.getState(this.state, 'checkout-button', ':active')
                  ? styles.buttonActive
                  : Radium.getState(this.state, 'checkout-button', ':hover')
                  ? styles.buttonHover
                  : null
              ] as any
            }
          >
            <p className="clear-margins primary fd-h4" style={styles.primary}>
              {message}
            </p>
          </button>
        </div>
      );
    } else {
      basketContents = (
        <div className="menu__basket__content menu__basket__content--empty text-center">
          <i
            className="tertiary-light icon_cart"
            style={isDark ? styles.primaryDark : styles.tertiaryLight}
            aria-hidden="true"
          />
          <p
            tabIndex={0}
            className="tertiary-light text-center fd-h4"
            style={isDark ? styles.primaryDark : styles.tertiaryLight}
          >
            {translate('Your_basket_is_empty')}
          </p>
        </div>
      );
    }

    // className affix
    return (
      <AutoAffix viewportOffsetTop={Offset} container={parentComponent}>
        <div
          className="hidden-sm hidden-xs menu__basket"
          style={isDark ? styles.darkBasket : styles.basket}
        >
          <div
            className="menu__basket__header flex flex-align-center flex-justify-between"
            style={styles.basketHeader}
          >
            <label
              tabIndex={0}
              aria-label={`${translate('Basket')} ${total}`}
              accessKey="b"
              className="primary clear-margins fd-h3"
              style={styles.primary}
            >
              {translate('Basket')}
            </label>
            <p className="primary clear-margins fd-h3" style={styles.primary}>
              {total}
            </p>
          </div>
          {basketContents}
        </div>
      </AutoAffix>
    );
  }
}

export default Radium(Basket);
